import React, { ComponentType, Dispatch, memo, ReactNode, SetStateAction, useState } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import PropTypes from 'prop-types';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';

export type PaymentContextAuthType = 'Google' | 'Facebook' | 'AAD' | 'MSA';

export type PaymentInstrumentType =
  | 0 // Unset
  | 1 // CreditCard
  | 2 // Invoice
  | 3 // Check
  | 4 // ElectronicFundsTransfer
  | 5 // PayPal
  | 6 // ELV
  | 7 // OfflinePaymentMethod
  | 8 // VBA
  | 9; // Boleto

export type AccountPaymentType =
  | 0 // PostPay
  | 1; // PrePay

export interface PaymentInstrumentConfig {
  id: PaymentInstrumentType;
  displayOrder: number;
  supportsRecurringPayments: boolean;
  supportedPaymentType: AccountPaymentType | null;
  supportedCurrencies: string[];
  useSupportedCountryList: boolean;
  supportedCountries?: string[] | null;
}

export interface PaymentContextData {
  AuthType?: PaymentContextAuthType;
  PidlConfiguration?: Record<string, unknown> | null;
  GreenIdUrl?: unknown;
  CookieDomain?: unknown;
  CookiePath?: unknown;
  PaymentInstrumentConfigs?: PaymentInstrumentConfig[];
  SupportedPiTypes?: PaymentInstrumentType[];
  PaymentError?: string | null;
  PaymentInstrumentId?: number | null;
  SmartCountriesDisabledForCreditCardAddFunds?: string[];
  ccl?: string | null;
  cco?: string | null;
  [key: string]: unknown;
}

export interface PaymentContextValue {
  paymentContext: PaymentContextData;
  setPaymentContext?: Dispatch<SetStateAction<PaymentContextData>>;
}

export interface PaymentContextProviderProps {
  children: ReactNode;
  paymentContext?: PaymentContextData;
}

export const PaymentContext = React.createContext<PaymentContextValue>({ paymentContext: window.paymentContext || {} });

const PaymentContextProviderUnwrapped = ({
  children,
  paymentContext,
}: PaymentContextProviderProps) => {
  const [state, setState] = useState<PaymentContextData>(paymentContext as PaymentContextData);
  return (
    <PaymentContext.Provider value={{ paymentContext: state, setPaymentContext: setState }}>
      {children}
    </PaymentContext.Provider>
  );
};

PaymentContextProviderUnwrapped.displayName = 'PaymentContextProvider';
PaymentContextProviderUnwrapped.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  paymentContext: PropTypes.object,
};
PaymentContextProviderUnwrapped.defaultProps = {
  paymentContext: {},
};
export const PaymentContextProvider = memo(PaymentContextProviderUnwrapped);

/**
 * Wraps an element with PaymentContext,
 * providing the ability to read payment context without deep passing values via props
 * @param {Component} WrappedComponent - Component to wrap.
 * @param {Object} [initialValue] - initial value of paymentContext.
 * @returns {Component} Component with PaymentContext.
 */
export const withPaymentContextProvider = <P extends object>(
  WrappedComponent: ComponentType<P>,
  initialValue?: PaymentContextData
) => {
  const HoC = memo((props: P) => (
    <PaymentContextProvider paymentContext={initialValue}>
      <WrappedComponent {...props} />
    </PaymentContextProvider>
  ));

  HoC.displayName = `withPaymentContextProvider(${getDisplayName(WrappedComponent)})`;

  return hoistNonReactStatics(HoC, WrappedComponent);
};
